import { FC, useState } from 'react';
import { IonList, useIonAlert } from '@ionic/react';
import { ViewNoteSection } from './ViewNoteSection';
import { INote } from '../generic-notes/INote';
import { ListUlIcon, EditIcon, DeleteIcon, CircleCheckIcon, ArrowRightIcon, ArrowLeftIcon } from '../../theme/Icons';
import { ActionItems } from './ActionItems';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { TasksState, ITask } from '../../tasks/TasksState';
import { TaskListItem } from '../../tasks/TaskListItem';
import { IAction } from '../../components/Menu';
import { HistoryViewModel } from '../../support/HistoryViewModel';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { Logger } from '../../support/Logger';

export const SmartTasks: FC<{ note: INote }> = ({ note }) => {
    const tasksState = useInstance(TasksState);
    const history = useInstance(HistoryViewModel);
    const mixpanel = useInstance(MixPanelEventEmitter);
    const log = useInstance(Logger);

    const [presentAlert] = useIonAlert();
    const [loadingOperations, setLoadingOperations] = useState<Set<string>>(new Set());

    useSubscription(() => tasksState);

    const recording = note.otherAttributes?.recording;

    // Filter tasks by this note
    const allTasks = tasksState.getTasks();
    const noteTasks = allTasks
        .filter(task => {
            return (task.source?.noteId && task.source.noteId === note.noteId);
        })
        .sort((a, b) => {
            // Sort by due date: earliest first
            const dateA = new Date(a.dueDate || '').getTime();
            const dateB = new Date(b.dueDate || '').getTime();

            if (dateA !== dateB) {
                return dateA - dateB; // Earlier dates first
            }

            // Same date - sort by display order if available
            if (a.displayOrder !== undefined && b.displayOrder !== undefined) {
                return a.displayOrder - b.displayOrder;
            }

            return 0;
        });

    // Backwards compatibility: fall back to ActionItems for old notes
    if (recording?.summary?.actionItems?.length && noteTasks.length === 0) {
        return <ActionItems note={note} />;
    }

    // If no tasks exist, don't show the section
    if (noteTasks.length === 0) {
        return null;
    }

    const handleTaskClick = (taskId: string) => {
        // Open task editor - navigate to tasks page with edit drawer
        const currentPath = history.getCurrentLocation().pathname;
        const careMapMatch = currentPath.match(/\/notes\/([^/]+)/);
        const careMapId = careMapMatch ? careMapMatch[1] : tasksState.getCareMapId();

        if (careMapId) {
            history.push(`/calendar/tasks/${careMapId}?edit=${taskId}&source=note-view`);
        }
    };

    const handleViewAllTasks = () => {
        mixpanel.track('View All Tasks Opened', () => ({
            'Source': 'note-view'
        }));

        const currentPath = history.getCurrentLocation().pathname;
        const careMapMatch = currentPath.match(/\/notes\/([^/]+)/);
        const careMapId = careMapMatch ? careMapMatch[1] : tasksState.getCareMapId();

        if (careMapId) {
            history.push(`/calendar/tasks/${careMapId}`);
        }
    };

    const handleToggleComplete = async (taskId: string, event?: any) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        await tasksState.toggleTaskCompletion(taskId);
    };

    const handleDeleteTask = (task: ITask) => {
        const destructiveRole = 'destructive';

        // Special confirmation for active recurring tasks
        const isActiveRecurringTask = task.recurrenceRule && !task.completed;
        const message = isActiveRecurringTask
            ? `"${task.description}" is a recurring task. Deleting will stop all future recurrences. You can skip it instead if you just want to skip this instance. Continue with delete?`
            : `"${task.description}" will be deleted and cannot be recovered.`;

        presentAlert({
            header: 'Delete Task?',
            message: message,
            cssClass: 'drawer-alert',
            id: 'delete-task-confirmation',
            buttons: [{
                text: 'Delete',
                role: destructiveRole,
                handler: () => {
                    tasksState.deleteTask(task.taskId);
                }
            }, {
                text: 'Cancel',
                role: 'cancel'
            }]
        });
    };

    const handleSkipTask = async (task: ITask) => {
        const operationId = `skip-${task.taskId}`;
        setLoadingOperations(prev => new Set(prev).add(operationId));
        try {
            await tasksState.skipTask(task.taskId);
        } catch (error: any) {
            error.message = `Failed to skip task: ${error.message}`;
            log.error(error);
            presentAlert({
                header: 'Skip Failed',
                message: 'Failed to skip this task. Please try again.',
                cssClass: 'drawer-alert',
                buttons: [{
                    text: 'OK',
                    role: 'cancel'
                }]
            });
        } finally {
            setLoadingOperations(prev => {
                const newSet = new Set(prev);
                newSet.delete(operationId);
                return newSet;
            });
        }
    };

    const handleUnskipTask = async (task: ITask) => {
        try {
            await tasksState.unskipTask(task.taskId);
        } catch (error: any) {
            error.message = `Failed to undo skip task: ${error.message}`;
            log.error(error);
            presentAlert({
                header: 'Undo Skip Failed',
                message: 'Failed to undo skip for this task. Please try again.',
                cssClass: 'drawer-alert',
                buttons: [{
                    text: 'OK',
                    role: 'cancel'
                }]
            });
        }
    };

    const handleCompleteForever = async (task: ITask) => {
        const operationId = `complete-forever-${task.taskId}`;
        setLoadingOperations(prev => new Set(prev).add(operationId));
        try {
            await tasksState.completeForever(task.taskId);
        } catch (error: any) {
            error.message = `Failed to complete forever: ${error.message}`;
            log.error(error);
            presentAlert({
                header: 'Complete Forever Failed',
                message: 'Failed to complete this task forever. Please try again.',
                cssClass: 'drawer-alert',
                buttons: [{
                    text: 'OK',
                    role: 'cancel'
                }]
            });
        } finally {
            setLoadingOperations(prev => {
                const newSet = new Set(prev);
                newSet.delete(operationId);
                return newSet;
            });
        }
    };

    const getTaskActions = (task: ITask): IAction[] => {
        const actions: IAction[] = [];

        // Always show edit if task has self link (edit permission)
        if (task.links?.self) {
            actions.push({
                Icon: EditIcon,
                text: 'Edit',
                handler: () => handleTaskClick(task.taskId)
            });
        }

        // Show skip option for all active tasks (recurring and non-recurring)
        if (!task.completed && !task.skippedAt && task.links?.self) {
            actions.push({
                Icon: ArrowRightIcon,
                text: 'Skip',
                handler: () => handleSkipTask(task)
            });
        }

        // Show undo skip option for skipped tasks (recurring or not)
        if (task.skippedAt && !task.completed && task.links?.self) {
            actions.push({
                Icon: ArrowLeftIcon,
                text: 'Undo Skip',
                handler: () => handleUnskipTask(task)
            });
        }

        // Show "Complete Forever" for recurring tasks that are active (not completed or skipped)
        if (task.recurrenceRule && !task.completed && !task.skippedAt && task.links?.self) {
            actions.push({
                Icon: CircleCheckIcon,
                text: 'Complete Forever',
                handler: () => handleCompleteForever(task)
            });
        }

        // Only show delete if task has delete link (delete permission)
        if (task.links?.delete) {
            actions.push({
                Icon: DeleteIcon,
                text: 'Delete',
                role: 'destructive',
                handler: () => handleDeleteTask(task)
            });
        }

        return actions;
    };

    return (
        <ViewNoteSection
            title="Smart Tasks"
            Icon={ListUlIcon}
            showAIIcon
            actionLink={{
                text: 'View All Tasks',
                onClick: handleViewAllTasks
            }}
            data-id="smart-tasks-section"
        >
            <IonList data-id="smart-tasks-list">
                {noteTasks.map(task => {
                    const isLoading = loadingOperations.has(`skip-${task.taskId}`) ||
                                     loadingOperations.has(`complete-forever-${task.taskId}`);

                    return (
                        <TaskListItem
                            key={task.taskId}
                            task={task}
                            zone="note"
                            isLoading={isLoading}
                            onTaskClick={handleTaskClick}
                            onToggleComplete={handleToggleComplete}
                            getTaskActions={getTaskActions}
                            showDateDisplay={true}
                        />
                    );
                })}
            </IonList>
        </ViewNoteSection>
    );
};
